import { useMemo } from 'react';

import { useCurrentStepName } from 'hooks/useCurrentStepName';
import { useLead } from 'providers/LeadProvider';
import { useSteps } from 'providers/StepsProvider';

type ResolveMonitoringInfoOptions = Pick<
  ReturnType<typeof useSteps>,
  'steps' | 'current'
> & {
  currentStepName: ReturnType<typeof useCurrentStepName>;
};

/**
 * Resolves monitoring information for a given signup step.
 *
 * Depending on the current step name, returns a specific monitoring info object.
 * - For the 'email-validation' step, returns monitoring index 0 and version 'v2'.
 * - For the 'offer-decline' step, returns monitoring index 6.2 and version 'v2'.
 * - For all other steps, returns the current step object from the steps array.
 *
 * @param options - An object containing the steps array, the current step index, and the current step name.
 * @param options.steps - The array of step objects.
 * @param options.current - The index of the current step in the steps array.
 * @param options.currentStepName - The name of the current step.
 * @returns The monitoring information object for the current step.
 */
function resolveEventMonitoringInfo({
  steps,
  current,
  currentStepName,
}: ResolveMonitoringInfoOptions) {
  const currentStep = steps[current];

  if (currentStepName === 'email-validation') {
    return {
      version: currentStep?.version ?? 'v2',
      name: 'email-validation',
    } as const;
  }

  if (currentStepName === 'status') {
    return {
      version: currentStep?.version ?? 'v2',
      name: 'status',
    } as const;
  }

  if (currentStepName === 'offer-decline') {
    return {
      version: currentStep?.version ?? 'v2',
      name: 'offer-decline',
    } as const;
  }

  return currentStep;
}

export function useEventData() {
  const { current, steps } = useSteps();
  const { selfSignUpId, lead } = useLead();
  const currentStepName = useCurrentStepName();

  return useMemo(() => {
    const currentStep = resolveEventMonitoringInfo({
      steps,
      current,
      currentStepName,
    });

    const status =
      currentStepName === 'status'
        ? {
            status: lead?.status,
            statusReason: lead?.statusReason,
          }
        : {};

    return {
      category: currentStep?.name?.replaceAll('-', '_') ?? 'empty',
      contextData: {
        stepVersion: currentStep?.version ?? 'v0',
        selfSignUpId: selfSignUpId ?? 'empty',
        leadId: lead?.id ?? 'empty',
        businessEmail: lead?.businessEmail ?? 'empty',
        ...status,
      },
    };
  }, [
    steps,
    current,
    currentStepName,
    selfSignUpId,
    lead?.id,
    lead?.businessEmail,
    lead?.status,
    lead?.statusReason,
  ]);
}
